import React, { useEffect, useRef, useState } from 'react';
import { IonContent, IonPage, useIonViewDidLeave } from '@ionic/react';
import { useTranslation } from 'react-i18next';
import { NatalChartResultDto, PublicNatalChartRequestDto, Planets } from '../api';
import AstroChart from '../components/AstroChart';
import { natalChartToUI } from '../components/astro/adapters/natalChartToAstroChart';
import { BirthDateInput } from '../components/ui/birth-date-input';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { TimeInput } from '../components/ui/time-input';
import {
  BirthDateDraft,
  EMPTY_BIRTH_DATE_DRAFT,
  maxBirthDate,
  parseBirthDate,
  parseBirthTime,
} from '../helpers/birthDateTime';
import { BirthPlaceSuggestion, searchBirthPlaceSuggestions } from '../features/birthPlaceSearch';
import { ascendantGuideUrl } from '../features/ascendantGuide';
import { sunGuideUrl } from '../features/sunGuide';
import useAstroApiClient from '../hooks/useAstroApiClient';
import { ArrowRight, Compass, Globe, Loader2, LockKeyhole, Moon, ShieldCheck, Sparkles, Sun } from 'lucide-react';
import './FreeNatalChart.css';

// Os mesmos quatro idiomas da landing e dos guias, cada um no seu proprio nome:
// quem procura a sua lingua nao a reconhece traduzida para a lingua atual.
const LANGUAGES = [
  { code: 'pt', name: 'Português' },
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Español' },
  { code: 'fr', name: 'Français' },
];

const FreeNatalChart: React.FC = () => {
  const { t, i18n } = useTranslation();
  const api = useAstroApiClient();
  const [date, setDate] = useState<BirthDateDraft>(EMPTY_BIRTH_DATE_DRAFT);
  const [time, setTime] = useState('');
  const [showFieldErrors, setShowFieldErrors] = useState(false);
  const [query, setQuery] = useState('');
  const [place, setPlace] = useState<BirthPlaceSuggestion>();
  const [suggestions, setSuggestions] = useState<BirthPlaceSuggestion[]>([]);
  const [chart, setChart] = useState<NatalChartResultDto>();
  const [busy, setBusy] = useState(false);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState('');
  const generation = useRef(0);
  const langMenu = useRef<HTMLDetailsElement>(null);
  const language = (i18n.resolvedLanguage || 'pt').split('-')[0];

  const clear = () => {
    generation.current += 1;
    setDate(EMPTY_BIRTH_DATE_DRAFT); setTime(''); setShowFieldErrors(false);
    setQuery(''); setPlace(undefined);
    setSuggestions([]); setChart(undefined); setError(''); setBusy(false);
  };
  useIonViewDidLeave(clear);
  useEffect(() => {
    const discard = () => clear();
    window.addEventListener('pagehide', discard);
    return () => { generation.current += 1; window.removeEventListener('pagehide', discard); };
  }, []);

  // O <details> abre e fecha sozinho; isto so acrescenta o que ele nao faz —
  // fechar ao clicar fora e com Escape, como no seletor da landing.
  useEffect(() => {
    const away = (event: Event) => {
      const menu = langMenu.current;
      if (menu?.open && !menu.contains(event.target as Node)) menu.open = false;
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && langMenu.current) langMenu.current.open = false;
    };
    document.addEventListener('click', away);
    document.addEventListener('keydown', escape);
    return () => { document.removeEventListener('click', away); document.removeEventListener('keydown', escape); };
  }, []);

  useEffect(() => { document.documentElement.lang = language; }, [language]);

  const chooseLanguage = (code: string) => {
    if (langMenu.current) langMenu.current.open = false;
    if (code !== language) i18n.changeLanguage(code);
  };

  useEffect(() => {
    if (place || query.trim().length < 3) { setSuggestions([]); setSearching(false); return; }
    let active = true;
    setSearching(true);
    const timer = window.setTimeout(async () => {
      try {
        const results = await searchBirthPlaceSuggestions(api, query.trim());
        if (active) { setSuggestions(results); setError(results.length ? '' : 'freeChart_noPlaces'); }
      } catch { if (active) setError('freeChart_searchError'); }
      finally { if (active) setSearching(false); }
    }, 350);
    return () => { active = false; window.clearTimeout(timer); };
  }, [api, query, place]);

  // Boundary imposed by the public endpoint: any past date, never a future one.
  const today = maxBirthDate();
  const dateOptions = { required: true, max: today };
  const dateResult = parseBirthDate(date, dateOptions);
  const timeResult = parseBirthTime(time, true);

  const calculate = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!place || busy) return;
    if (dateResult.status !== 'valid' || timeResult.status !== 'valid') {
      setShowFieldErrors(true);
      return;
    }
    const current = ++generation.current;
    setBusy(true); setError(''); setChart(undefined);
    try {
      const result = await api.apiPublicNatalChart(new PublicNatalChartRequestDto({
        birthDate: dateResult.value, birthTime: timeResult.value,
        latitude: place.latitude, longitude: place.longitude,
      }));
      if (current === generation.current) setChart(result);
    } catch (failure) {
      if (current === generation.current) setError(
        (failure as { status?: number }).status === 429 ? 'freeChart_rateLimit' : 'freeChart_error');
    } finally { if (current === generation.current) setBusy(false); }
  };

  const ui = chart ? natalChartToUI(chart) : undefined;
  const homeUrl = `https://senti.pt${language === 'pt' ? '/' : `/${language}/`}`;
  const summaryIcons = { sun: Sun, moon: Moon, ascendant: Compass };
  return <IonPage className="free-chart-page"><IonContent className="free-chart-content">
    <header className="free-chart-header">
      <div className="free-chart-container free-chart-header-inner">
        {/* GR-006: a mesma marca dos guias (landing/templates/content-*.html) —
            a estrela da landing com o brilho por cima. Com o cabecalho reduzido
            a marca e idioma, este link e a unica saida da pagina. */}
        <a href={homeUrl} className="free-chart-brand" onClick={clear}>
          <span className="free-chart-brand-mark" aria-hidden="true">
            <svg className="free-chart-brand-star" viewBox="0 0 64 64"><path d="M32 10.5 38.63 23.94 53.47 26.1 42.74 36.56 45.27 51.34 32 44.36 18.73 51.34 21.26 36.56 10.53 26.1 25.37 23.94 32 10.5Z" fill="#F9BF2D" stroke="#F59E0B" strokeWidth="1.5" strokeLinejoin="round" /></svg>
            <svg className="free-chart-brand-sparkle" viewBox="0 0 64 64"><path d="M32 4 38.5 25.5 60 32 38.5 38.5 32 60 25.5 38.5 4 32 25.5 25.5 32 4Z" fill="currentColor" /></svg>
          </span>
          <span className="free-chart-brand-text"><strong>Senti</strong><span>{t('personalizedCosmicJourney')}</span></span>
        </a>
        <details className="free-chart-lang" ref={langMenu}>
          <summary aria-label={t('language')}><Globe size={14} aria-hidden="true" />{language.toUpperCase()}</summary>
          <div className="free-chart-lang-menu">
            {LANGUAGES.map(item => <button key={item.code} type="button" lang={item.code}
              className={item.code === language ? 'active' : undefined}
              aria-current={item.code === language ? 'true' : undefined}
              onClick={() => chooseLanguage(item.code)}>{item.name}</button>)}
          </div>
        </details>
      </div>
    </header>
    <main>
      <section className="free-chart-hero">
        <div className="free-chart-container">
          <span className="free-chart-eyebrow"><Sparkles size={16} aria-hidden="true" />{t('freeChart_noAccount')}</span>
          <h1>{t('freeChart_title')}</h1>
          <p>{t('freeChart_intro')}</p>
        </div>
      </section>
      <div className="free-chart-container free-chart-body">
      <section className="free-chart-entry" aria-labelledby="free-chart-form-title">
      <form onSubmit={calculate} autoComplete="off" className="free-chart-form">
        <h2 id="free-chart-form-title">{t('freeChart_formTitle')}</h2>
        <p className="free-chart-form-intro">{t('freeChart_formIntro')}</p>
        <p className="free-chart-mobile-boundary">{t('freeChart_boundary')}</p>
        <fieldset disabled={busy}>
          <div className="free-chart-birth-fields">
            {/* The anonymous tool never offers the visitor's own birthday for autofill. */}
            <BirthDateInput id="free-date" language={language} autoComplete="off"
              required max={today} showErrors={showFieldErrors}
              value={date} onValueChange={next => { setDate(next); setChart(undefined); }} />
            <TimeInput id="free-time" language={language} required showErrors={showFieldErrors}
              value={time} onValueChange={next => { setTime(next); setChart(undefined); }} />
          </div>
          <label htmlFor="free-place">{t('freeChart_place')}
            <Input id="free-place" required value={query} maxLength={120} onChange={e => { setQuery(e.target.value); setPlace(undefined); setChart(undefined); setError(''); }} aria-describedby="free-place-help" />
          </label>
          <p id="free-place-help" className="free-chart-help">{t('freeChart_placeHelp')}</p>
          {searching && <p role="status" className="free-chart-search-status"><Loader2 size={16} className="animate-spin" aria-hidden="true" />{t('freeChart_searching')}</p>}
          {suggestions.length > 0 && <ul className="free-chart-suggestions">{suggestions.map(suggestion =>
            <li key={suggestion.id}><button type="button" onClick={() => { setPlace(suggestion); setQuery(suggestion.displayName); setSuggestions([]); setError(''); }}>{suggestion.displayName}</button></li>)}</ul>}
          <Button className="free-chart-primary" type="submit" disabled={!place || busy}>
            {busy ? <Loader2 size={18} className="animate-spin" aria-hidden="true" /> : <Sparkles size={18} aria-hidden="true" />}
            {t(busy ? 'freeChart_calculating' : 'freeChart_calculate')}
            {!busy && <ArrowRight size={18} aria-hidden="true" />}
          </Button>
        </fieldset>
        {error && <p role="alert" className="free-chart-error">{t(error)}</p>}
      </form>
      <aside className="free-chart-details">
        <div><span className="free-chart-detail-icon"><Compass size={22} aria-hidden="true" /></span>
          <h2>{t('freeChart_included')}</h2><p>{t('freeChart_boundary')}</p>
        </div>
        <div className="free-chart-privacy"><ShieldCheck size={22} aria-hidden="true" />
          <div><h3>{t('freeChart_privateTitle')}</h3><p>{t('freeChart_privacy')}</p></div>
        </div>
      </aside>
      </section>
      {chart && ui && <section aria-label={t('freeChart_result')} className="free-chart-results">
        <div className="free-chart-result-heading"><div><span className="free-chart-kicker">Senti</span><h2>{t('freeChart_result')}</h2></div>
          <Button variant="outline" className="free-chart-secondary" onClick={clear}>{t('freeChart_clear')}</Button>
        </div>
        <div className="free-chart-result-grid">
        <div className="free-chart-wheel"><AstroChart
          planets={[{ name: Planets.Sun, ...ui.chartData.sun }, { name: Planets.Moon, ...ui.chartData.moon }, ...ui.chartData.planets]}
          aspects={ui.aspects} houses={ui.chartData.houses} ascendant={ui.chartData.ascendant}
          descendant={ui.chartData.descendant} mediumCoeli={ui.chartData.mediumCoeli} imumCoeli={ui.chartData.imumCoeli}
        /></div>
        <div className="free-chart-summaries">
          {(['sun', 'moon', 'ascendant'] as const).map(key => {
            const Icon = summaryIcons[key];
            const guideUrl = key === 'sun' ? sunGuideUrl(ui.chartData.sun.sign, language)
              : key === 'ascendant' ? ascendantGuideUrl(ui.chartData.ascendant.sign, language) : undefined;
            const content = <>
              <Icon size={23} aria-hidden="true" />
              <h3>{t(`freeChart_${key}`)}: {t(`sign_${ui.chartData[key].sign}`)}</h3>
              <p>{t(`freeChart_${key}Summary`, { sign: t(`sign_${ui.chartData[key].sign}`) })}</p>
              {guideUrl && <span className="free-chart-guide-cta">{t('freeChart_readGuide')}<ArrowRight size={18} aria-hidden="true" /></span>}
            </>;
            const className = `free-chart-summary free-chart-summary-${key}`;
            return guideUrl
              ? <a key={key} href={guideUrl} className={`${className} free-chart-guide`} onClick={clear} referrerPolicy="no-referrer">{content}</a>
              : <div key={key} className={className}>{content}</div>;
          })}
        </div>
        </div>
        <p className="free-chart-symbolic">{t('freeChart_symbolic')}</p>
      </section>}
      <section className="free-chart-account">
        <div><h2>{t('freeChart_continue')}</h2><p><LockKeyhole size={16} aria-hidden="true" />{t('freeChart_privacy')}</p></div>
        <a className="free-chart-primary" onClick={clear}
          href={`/dashboard?utm_source=senti&utm_medium=free-tool&utm_campaign=gr006-mapa-gratis&lng=${encodeURIComponent(i18n.resolvedLanguage || 'pt')}`}>
          {t('freeChart_register')}<ArrowRight size={18} aria-hidden="true" />
        </a>
      </section>
      </div>
    </main>
  </IonContent></IonPage>;
};

export default FreeNatalChart;
